import { PathPatternParser } from '../path-pattern-parser.ts';
import { PathPattern } from '../path-pattern.ts';
import { PathElement } from './path-element.ts';
import { SeparatorPathElement } from './separator-path-element.ts';
import { WildcardPathElement } from './wildcard-path-element.ts';
import { CaptureSegmentsPathElement } from './capture-segments-path-element.ts';
import { PatternParseError } from '../pattern-parse-error.ts';
import { WildcardSegmentsPathElement } from './wildcard-segments-path-element.ts';
import { CaptureVariablePathElement } from './capture-variable-path-element.ts';
import { SingleCharWildcardedPathElement } from './single-char-wildcarded-path-element.ts';
import { RegexPathElement } from './regex-path-element.ts';
import { LiteralPathElement } from './literal-path-element.ts';

function isJavaScriptIdentifierStartChar(ch: number) {
    return (ch === 36 || ch === 95 || // $ and _
        (ch >= 65 && ch <= 90) || // A..Z
        (ch >= 97 && ch <= 122)); // a..z;
}


function isJavaScriptIdentifierPartChar(ch: number) {
    return (ch === 36 || ch === 95 || // $ and _
        (ch >= 65 && ch <= 90) || // A..Z
        (ch >= 97 && ch <= 122) || // a..z;
        (ch >= 48 && ch <= 57)); // 0..9;
}

/*internal*/ export class InternalPathPatternParser {
    readonly #parser: PathPatternParser;

    // the input data for parsing
    #pathPatternData: Uint16Array = new Uint16Array(0);
    // the length of the input data
    #pathPatternLength: number = 0;
    // current parsing position
    #pos: number = 0;
    // how many ? characters in a particular path element
    #singleCharWildcardCount: number = 0;
    // is the path pattern using * characters in a particular path element
    #wildcard: boolean = false;
    // is the construct {*...} being used in a particular path element
    #isCaptureSegmentsVariable: boolean = false;
    // has the parser entered a  {...}  capture variable block in a particular path element
    #insideVariableCapture: boolean = false;
    // how many variable captures are occurring in a particular path element
    #variableCaptureCount: number = 0;
    // start of the most recent path element in a particular path element
    #pathElementStart: number = 0;
    // start of the most recent variable capture in a particular path element
    #variableCaptureStart: number = 0;
    // did we parse a WildcardSegments(**) or CaptureSegments({*foo}) PathElement already?
    #hasMultipleSegmentsElements: boolean = false;
    // variables captured in this path pattern
    #capturedVariableNames?: string[];
    // the head of the path element chain currently being built
    #headPE?: PathElement;
    // the most recently constructed path element in the chain
    #currentPE?: PathElement;

    constructor(parser: PathPatternParser) {
        this.#parser = parser;
    }

    parse(pathPattern: string): PathPattern {
        if (pathPattern === undefined || pathPattern === null) throw new TypeError(`pathPattern must be defined`);

        this.#pathPatternData = new Uint16Array(pathPattern.length);
        for (let i = 0; i < pathPattern.length; i++) {
            this.#pathPatternData[i] = pathPattern.charCodeAt(i);
        }
        this.#pathPatternLength = this.#pathPatternData.length;
        this.#headPE = undefined;
        this.#currentPE = undefined;
        this.#capturedVariableNames = undefined;
        this.#pathElementStart = -1;
        this.#pos = 0;
        this.#resetPathElementState();

        while (this.#pos < this.#pathPatternLength) {
            const ch = this.#pathPatternData[this.#pos];
            const separator = this.#parser.pathOptions.separator;
            if (ch === separator) {
                if (this.#pathElementStart !== -1) {
                    this.#pushPathElement(this.#createPathElement());
                }
                this.#pushPathElement(new SeparatorPathElement(this.#pos, separator));
            }
            else {
                if (this.#pathElementStart === -1) {
                    this.#pathElementStart = this.#pos;
                }
                if (ch === '?'.charCodeAt(0)) {
                    this.#singleCharWildcardCount++;
                }
                else if (ch === '{'.charCodeAt(0)) {
                    if (this.#insideVariableCapture) {
                        throw PatternParseError.create(this.#pos, this.#pathPatternData, 'ILLEGAL_NESTED_CAPTURE');
                    }
                    // if we enforced that adjacent captures weren't allowed,
                    // this would do it (this would be and error: /foo/{bar}{boo}/)
                    // } else if (this.#pos > 0 && this.#pathPatternData[this.#pos - 1] === '}'.charCodeAt(0)) {
                    //     throw new PatternParseError(this.#pos, this.#pathPatternData, PatternMessage.CANNOT_HAVE_ADJACENT_CAPTURES);
                    // }
                    this.#insideVariableCapture = true;
                    this.#variableCaptureStart = this.#pos;
                }
                else if (ch === '}'.charCodeAt(0)) {
                    if (!this.#insideVariableCapture) {
                        throw PatternParseError.create(this.#pos, this.#pathPatternData, 'MISSING_OPEN_CAPTURE');
                    }
                    this.#insideVariableCapture = false;
                    this.#variableCaptureCount++;
                }
                else if (ch === ':'.charCodeAt(0)) {
                    if (this.#insideVariableCapture && !this.#isCaptureSegmentsVariable) {
                        this.#skipCaptureRegex();
                        this.#insideVariableCapture = false;
                        this.#variableCaptureCount++;
                    }
                }
                else if (this.#isDoubleWildcard(separator)) {
                    this.#checkValidMultipleSegmentsElements(this.#pos, this.#pos + 1);
                    this.#pushPathElement(new WildcardSegmentsPathElement(this.#pos, separator));
                    this.#hasMultipleSegmentsElements = true;
                    this.#pos++;
                }
                else if (ch === '*'.charCodeAt(0)) {
                    if (this.#insideVariableCapture && this.#variableCaptureStart == this.#pos - 1) {
                        this.#isCaptureSegmentsVariable = true;
                    }
                    this.#wildcard = true;
                }
                if (this.#insideVariableCapture) {
                    if ((this.#variableCaptureStart + 1 + (this.#isCaptureSegmentsVariable ? 1 : 0)) === this.#pos &&
                            !isJavaScriptIdentifierStartChar(ch)) {
                        throw PatternParseError.create(this.#pos, this.#pathPatternData, 'ILLEGAL_CHARACTER_AT_START_OF_CAPTURE_DESCRIPTOR', String.fromCharCode(ch));
                    }
                    else if ((this.#pos > (this.#variableCaptureStart + 1 + (this.#isCaptureSegmentsVariable ? 1 : 0)) &&
                            !isJavaScriptIdentifierPartChar(ch) && ch !== '-'.charCodeAt(0))) {
                        throw PatternParseError.create(this.#pos, this.#pathPatternData, 'ILLEGAL_CHARACTER_IN_CAPTURE_DESCRIPTOR', String.fromCharCode(ch));
                    }
                }
            }
            this.#pos++;
        }
        if (this.#pathElementStart !== -1) {
            this.#pushPathElement(this.#createPathElement());
        }
        return new PathPattern(pathPattern, this.#parser, this.#headPE);
    }

    #getPathElementText(): Uint16Array {
        const pathElementText = new Uint16Array(this.#pos - this.#pathElementStart);
        let index = 0;
        for (let i = this.#pathElementStart; i < this.#pos; i++) {
            pathElementText[index++] = this.#pathPatternData[i];
        }
        return pathElementText;
    }

    #createPathElement(): PathElement {
        if (this.#insideVariableCapture) {
            throw PatternParseError.create(this.#pos, this.#pathPatternData, 'MISSING_CLOSE_CAPTURE');
        }

        let newPE: PathElement = undefined!;
        const separator = this.#parser.pathOptions.separator;

        if (this.#variableCaptureCount > 0) {
            if (this.#variableCaptureCount === 1 && this.#pathElementStart === this.#variableCaptureStart &&
                this.#pathPatternData[this.#pos - 1] === '}'.charCodeAt(0)) {
                if (this.#isCaptureSegmentsVariable) {
                    // It is {*....}
                    this.#checkValidMultipleSegmentsElements(this.#pathElementStart, this.#pos - 1);
                    this.#hasMultipleSegmentsElements = true;
                    newPE = new CaptureSegmentsPathElement(this.#pathElementStart, this.#getPathElementText(), separator);
                }
                else {
                    // It is a full capture of this element (possibly with constraint), for example: /foo/{abc}/
                    try {
                        newPE = new CaptureVariablePathElement(this.#pathElementStart, this.#getPathElementText(), this.#parser.caseSensitive, separator);
                    }
                    catch (err) {
                        if (err instanceof SyntaxError) {
                            throw PatternParseError.createWithCause(err,
                                this.#findRegExpStart(this.#pathPatternData, this.#pathElementStart) + (err['index'] ?? 0),
                                this.#pathPatternData, 'REGEX_PATTERN_SYNTAX_EXCEPTION');
                        }
                        throw err;
                    }
                    this.#recordCapturedVariable(this.#pathElementStart, (newPE as CaptureVariablePathElement).variableName);
                }
            }
            else {
                if (this.#isCaptureSegmentsVariable) {
                    throw PatternParseError.create(this.#pathElementStart, this.#pathPatternData, 'CAPTURE_ALL_IS_STANDALONE_CONSTRUCT');
                }
                const newRegexPE = new RegexPathElement(
                    this.#pathElementStart,
                    this.#getPathElementText(),
                    this.#parser.caseSensitive,
                    this.#pathPatternData, separator
                );
                for (const variableName of newRegexPE.variableNames) {
                    this.#recordCapturedVariable(this.#pathElementStart, variableName);
                }
                newPE = newRegexPE;
            }
        }
        else {
            if (this.#wildcard) {
                if (this.#pos - 1 === this.#pathElementStart) {
                    newPE = new WildcardPathElement(this.#pathElementStart, separator);
                }
                else {
                    newPE = new RegexPathElement(
                        this.#pathElementStart,
                        this.#getPathElementText(),
                        this.#parser.caseSensitive,
                        this.#pathPatternData,
                        separator
                    );
                }
            }
            else if (this.#singleCharWildcardCount !== 0) {
                newPE = new SingleCharWildcardedPathElement(
                    this.#pathElementStart,
                    this.#getPathElementText(),
                    this.#singleCharWildcardCount,
                    this.#parser.caseSensitive,
                    separator
                );
            }
            else {
                newPE = new LiteralPathElement(
                    this.#pathElementStart,
                    this.#getPathElementText(),
                    this.#parser.caseSensitive,
                    separator
                );
            }
        }

        return newPE;
    }

    #skipCaptureRegex() {
        this.#pos++;
        const regexStartPos = this.#pos;
        let curlyBracketDepth = 0; // how deep in nested {...} pairs we are
        let previousBackslash = false;

        while (this.#pos < this.#pathPatternLength) {
            const ch = this.#pathPatternData[this.#pos];
            if (ch === '\\'.charCodeAt(0) && !previousBackslash) {
                this.#pos++;
                previousBackslash = true;
                continue;
            }
            if (ch === '{'.charCodeAt(0) && !previousBackslash) {
                curlyBracketDepth++;
            }
            else if (ch === '}'.charCodeAt(0) && !previousBackslash) {
                if (curlyBracketDepth === 0) {
                    if (regexStartPos == this.#pos) {
                        throw PatternParseError.create(this.#pos, this.#pathPatternData, 'MISSING_REGEX_CONSTRAINT');
                    }
                    return;
                }
                curlyBracketDepth--;
            }
            if (ch == this.#parser.pathOptions.separator && !previousBackslash) {
                throw PatternParseError.create(this.#pos, this.#pathPatternData, 'MISSING_CLOSE_CAPTURE');
            }
            this.#pos++;
            previousBackslash = false;
        }
        throw PatternParseError.create(this.#pos - 1, this.#pathPatternData, 'MISSING_CLOSE_CAPTURE');
    }

    #isDoubleWildcard(separator: number): boolean {
        // next char is present
        if ((this.#pos + 1) >= this.#pathPatternLength) {
            return false;
        }
        // current char and next char are '*'
        if (this.#pathPatternData[this.#pos] !== '*'.charCodeAt(0) ||
            this.#pathPatternData[this.#pos + 1] !== '*'.charCodeAt(0)) {
            return false;
        }
        // previous char is separator, if any
        if ((this.#pos - 1 >= 0) && (this.#pathPatternData[this.#pos - 1] !== separator)) {
            return false;
        }
        // next char is separator, if any
        if ((this.#pos + 2 < this.#pathPatternLength) &&
            (this.#pathPatternData[this.#pos + 2] !== separator)) {
            return false;
        }
        return true;
    }

    #checkValidMultipleSegmentsElements(startPos: number, endPos: number) {
        if (this.#hasMultipleSegmentsElements) {
            throw PatternParseError.create(this.#pos, this.#pathPatternData, 'CANNOT_HAVE_MANY_MULTISEGMENT_PATH_ELEMENTS');
        }
        if (startPos > 1 && endPos !== (this.#pathPatternLength - 1)) {
            throw PatternParseError.create(this.#pos, this.#pathPatternData, 'INVALID_LOCATION_FOR_MULTISEGMENT_PATH_ELEMENT');
        }
    }

    #pushPathElement(newPathElement: PathElement) {
        if (newPathElement instanceof CaptureSegmentsPathElement ||
            newPathElement instanceof WildcardSegmentsPathElement) {
            // there must be a separator ahead of this thing
            // currentPE SHOULD be a SeparatorPathElement
            if (this.#currentPE === undefined) {
                this.#headPE = newPathElement;
                this.#currentPE = newPathElement;
            }
            else if (this.#currentPE instanceof SeparatorPathElement) {
                const peBeforeSeparator = this.#currentPE.prev;
                if (peBeforeSeparator === undefined) {
                    // {*foo} is at the start
                    this.#headPE = newPathElement;
                    newPathElement.prev = undefined;
                }
                else {
                    peBeforeSeparator.next = newPathElement;
                    newPathElement.prev = peBeforeSeparator;
                }
                this.#currentPE = newPathElement;
            }
            else {
                throw new Error(`expected SeparatorPathElement before ${newPathElement} but was ${this.#currentPE}`);
            }
        }
        else {
            if (this.#headPE === undefined) {
                this.#headPE = newPathElement;
                this.#currentPE = newPathElement;
            }
            else if (this.#currentPE !== undefined) {
                this.#currentPE.next = newPathElement;
                newPathElement.prev = this.#currentPE;
                this.#currentPE = newPathElement;
            }
        }

        this.#resetPathElementState();
    }

    #findRegExpStart(data: Uint16Array, offset: number): number {
        let pos = offset;
        while (pos < data.length) {
            if (data[pos] === ':'.charCodeAt(0)) {
                return pos + 1;
            }
            pos++;
        }
        return -1;
    }

    #resetPathElementState() {
        this.#pathElementStart = -1;
        this.#singleCharWildcardCount = 0;
        this.#insideVariableCapture = false;
        this.#variableCaptureCount = 0;
        this.#wildcard = false;
        this.#isCaptureSegmentsVariable = false;
        this.#variableCaptureStart = -1;
    }

    #recordCapturedVariable(pos: number, variableName: string) {
        if (this.#capturedVariableNames === undefined) {
            this.#capturedVariableNames = [];
        }
        if (this.#capturedVariableNames?.find(v => v === variableName) !== undefined) {
            throw PatternParseError.create(pos, this.#pathPatternData, 'ILLEGAL_DOUBLE_CAPTURE', variableName);
        }
        this.#capturedVariableNames.push(variableName);
    }
}
